import { useMemo } from 'react';

/**
 * useEvidenceTransform
 * Normalizes and transforms evidence data from various formats
 * Handles both single evidence and array of evidences
 * Prefers 1 image + 1 audio for display if mixed content exists
 *
 * @param {object|array} evidence - Single evidence object or array of evidence objects
 * @returns {object} { visibleItems, remainingCount, allItems, totalCount, hasMore }
 */
const useEvidenceTransform = (evidence) => {
  const transformed = useMemo(() => {
    // Handle null/undefined
    if (!evidence) {
      return {
        visibleItems: [],
        remainingCount: 0,
        allItems: [],
        totalCount: 0,
        hasMore: false,
      };
    }

    // Normalize to array
    const evidencesArray = Array.isArray(evidence)
      ? evidence
      : [evidence].filter(Boolean);

    // Flatten to get all image and audio items
    const allItems = [];
    const imageItems = [];
    const audioItems = [];

    evidencesArray.forEach((ev, index) => {
      if (!ev) return;

      if (ev.image) {
        const imageItem = {
          type: 'image',
          src: ev.image,
          id: ev.id || `image-${index}`,
          label: `Evidence ${index + 1}`,
          allItemsIndex: allItems.length, // Track actual index in allItems
        };
        allItems.push(imageItem);
        imageItems.push(imageItem);
      }

      if (ev.audio) {
        const audioItem = {
          type: 'audio',
          src: ev.audio,
          id: ev.id || `audio-${index}`,
          label: `Evidence ${index + 1}`,
          allItemsIndex: allItems.length, // Track actual index in allItems
        };
        allItems.push(audioItem);
        audioItems.push(audioItem);
      }
    });

    // Determine visible items: prefer 1 image + 1 audio if mixed content
    const visibleItems = [];
    const hasImages = imageItems.length > 0;
    const hasAudio = audioItems.length > 0;

    if (hasImages && hasAudio) {
      // Prefer 1 image + 1 audio for better UX
      visibleItems.push(imageItems[0]);
      visibleItems.push(audioItems[0]);
    } else if (hasImages) {
      // Only images: show up to 2
      visibleItems.push(...imageItems.slice(0, 2));
    } else if (hasAudio) {
      // Only audio: show up to 2
      visibleItems.push(...audioItems.slice(0, 2));
    }

    // Calculate remaining count
    const remainingCount = Math.max(0, allItems.length - visibleItems.length);

    return {
      visibleItems,
      remainingCount,
      allItems,
      totalCount: allItems.length,
      hasMore: remainingCount > 0,
    };
  }, [evidence]);

  return transformed;
};

export default useEvidenceTransform;
